import { useRef, useState } from "react";
import { api } from "../lib/api";

/**
 * Logo upload + preview + remove for an existing client.
 * Receives the client (with logo_bytes) and a setter so the parent can
 * react to changes without re-rendering the whole modal.
 *
 * Extracted from AdminClients/ClientModal to keep that component below
 * the cyclomatic-complexity cliff.
 */
export default function ClientLogoBlock({ client, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [logoBust, setLogoBust] = useState(0);
  const fileRef = useRef(null);

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setErr("");
    setBusy(true);
    try {
      await api.uploadClientLogo(client.client_id, f);
      setLogoBust(Date.now());
      onChanged?.();
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message || "Upload failed");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onRemove = async () => {
    if (!window.confirm("Remove this client's logo?")) return;
    setErr("");
    setBusy(true);
    try {
      await api.deleteClientLogo(client.client_id);
      setLogoBust(Date.now());
      onChanged?.();
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const hasLogo = client.logo_bytes > 0;
  const logoUrl = hasLogo
    ? `${process.env.REACT_APP_BACKEND_URL || ""}/api/clients/${client.client_id}/logo${logoBust ? `?t=${logoBust}` : ""}`
    : null;

  return (
    <div className="form-field form-field-wide cm-logo-block">
      <label>Logo</label>
      <div className="cm-logo-row">
        <div className="cm-logo-preview">
          {logoUrl ? (
            <img src={logoUrl} alt={`${client.client_id} logo`} data-testid="cm-logo-img" />
          ) : (
            <span className="muted">No logo uploaded</span>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            data-testid="cm-logo-file"
            onChange={onUpload}
            disabled={busy}
          />
          {logoUrl && (
            <button
              type="button"
              className="btn-ghost btn-sm"
              data-testid="cm-logo-remove"
              onClick={onRemove}
              disabled={busy}
            >Remove logo</button>
          )}
        </div>
      </div>
      {err && <div className="form-error" data-testid="cm-logo-error">{err}</div>}
    </div>
  );
}
